<template>
  <!--
    Click semantics differ between branches by design (matches blackswan-web):
    - SVG branch: `@click` so taps anywhere inside the icon emit
    - Webfont branch: `@click.self` so child pseudo-elements don't double-fire
  -->
  <component
    :is="p26Icon"
    v-if="p26Icon"
    :size="p26Size"
    :class="['p-icon', `t-${props.color}`, `p-icon--${props.size}`]"
    @click="$emit('on-click')"
  />
  <i v-else v-bind="iconAttributes" @click.self="$emit('on-click')" />
</template>

<script lang="ts" setup>
import type { Component } from 'vue'
import * as P26Icons from '@p-utilities/icons-vue'

const props = defineProps({
  icon: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: 'fg-accent',
  },
  size: {
    type: String,
    default: 'md',
    validator: (val: string) =>
      ['xs', 'sm', 'xsm', 'ms', 'xmd', 'md', 'lg', 'xl', 'xxl', 'xxxl'].includes(val),
  },
})

defineEmits(['on-click'])

// kebab → PascalCase: 'alarm' → 'Alarm', 'address-book' → 'AddressBook'
const toPascal = (s: string) => s.replace(/(^|-)(.)/g, (_, _dash, c) => c.toUpperCase())

const p26Icon = computed<Component | null>(() => {
  const key = toPascal(props.icon)
  return (P26Icons as Record<string, Component>)[key] ?? null
})

// support-web's 10 sizes mapped to P26 pixel sizes.
// xl/xxl/xxxl all cap at 40 — P26 SVGs have no larger size.
// In the webfont branch, p-icon--xxl / --xxxl CSS rules still apply for size differences there.
const sizeMap: Record<string, number> = {
  xs: 12,
  sm: 16,
  xsm: 16,
  ms: 20,
  xmd: 20,
  md: 24,
  lg: 32,
  xl: 40,
  xxl: 40,
  xxxl: 40,
}
const p26Size = computed(() => sizeMap[props.size] ?? 24)

const iconAttributes = computed(() => ({
  class: ['p-icon', `t-${props.color}`, `i-${props.icon}`, `p-icon--${props.size}`],
}))
</script>
